import { useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { businessApi } from '@/lib/api-paths';
import { readActiveTenant } from '@/lib/active-tenant';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import {
  PLANS,
  SURVEY_ADDON,
  SURVEY_WHITE_LABEL,
  TERMS,
  chargedPriceDisplay,
  formatChargedPrice,
  type PlanKey,
} from '@pageboss/shared';
import { PanelShell } from '@/components/panel-shell';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { Skeleton } from '@/components/skeleton';
import { useMe, invalidateMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import { appRoutes, businessRoutes, locationRoutes } from '@/lib/route-map';

// Invoice history (WP1 Task 13) — mirrors the server's StripeInvoiceRow shape
// (apps/dashboard/src/lib/stripe-api.ts). Defined locally rather than
// imported: the web bundle only pulls from @pageboss/shared/@pageboss/ui and
// its own `@/`-rooted modules, never from the server app's `src/`.
interface InvoiceRow {
  id: string;
  number: string | null;
  created: number;
  total: number;
  currency: string;
  status: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

function formatInvoiceAmount(totalCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(totalCents / 100);
  } catch {
    // An unrecognized currency code from Stripe (shouldn't happen) — fall
    // back to a plain dollar figure rather than throwing mid-render.
    return `$${(totalCents / 100).toFixed(2)}`;
  }
}

function invoiceStatusVariant(status: string): 'success' | 'warning' | 'destructive' | 'muted' {
  if (status === 'paid') return 'success';
  if (status === 'open') return 'warning';
  if (status === 'uncollectible' || status === 'void') return 'destructive';
  return 'muted';
}

// ?from= return link (T12's UpgradeGate links here with it). Must be an
// INTERNAL path only — never render an unvalidated redirect target. Rejects
// anything that doesn't start with a single '/', protocol-relative '//...',
// an embedded scheme ('...://...'), and the '/\...' trick some browsers
// normalize into '//...'.
//
// SECURITY FIX ROUND 1 (adversarial review, HIGH): the structural checks
// above ran against the raw decoded string, but browsers strip ASCII control
// characters (TAB/CR/LF and the rest of the C0 range) when RESOLVING an
// href. That let `?from=%2F%0A%2F%2Fevil.com` decode to "/\n//evil.com" —
// which starts with a single '/' and has no literal "//" or "://" as a
// STRING, passing every check above — while the browser's URL resolver
// treats it as "//evil.com" (protocol-relative) and navigates off-site
// (confirmed via WHATWG URL: `new URL("/\n//evil.com", base).href ===
// "https://evil.com/"`). Fix: reject outright if the candidate contains any
// control character, BEFORE the structural checks — simpler to reason about
// than stripping-then-revalidating, and a `from` value that ever contained a
// control character is untrustworthy regardless of what it decodes to.
const CONTROL_CHARS = /[\x00-\x1F\x7F]/;

export function safeInternalPath(raw: string | null): string | null {
  if (!raw) return null;
  if (CONTROL_CHARS.test(raw)) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  if (raw.includes('://')) return null;
  return raw;
}

// Outbound feature links per plan card (killing the billing page's
// "sink" problem — WP1 Task 13). Deliberately scoped to features that are
// ACTUALLY unlocked at that internal plan key, per CLAUDE.md's gating table:
// GBP insights/posts + map ranking + AI visibility + get-found start at
// `pro` (displayed "Starter"); review reply drafts are `business`+; multi-
// location is `growth`-only. `to(businessId)` — falls back to `/app` when
// there's no active business yet, so the link is never dead.
// Billing is a BUSINESS screen, but these are links INTO Location features, so
// each needs both ids. The Location is the one the browser remembers
// (`pb.active`) — a navigation preference, never authorization: the screen it
// opens resolves and proves the tenant itself, and with nothing remembered the
// link falls back to `/app`, which resolves deterministically.
interface FeatureLink {
  label: string;
  to: (businessId: string, locationId: string | null) => string;
}

const featureLink =
  (build: (b: string, l: string) => string) =>
  (b: string, l: string | null) =>
    l ? build(b, l) : appRoutes.index();

const BASE_FEATURE_LINKS: FeatureLink[] = [
  { label: 'Website & pages', to: featureLink(locationRoutes.website) },
  { label: TERMS.googleProfile, to: featureLink(locationRoutes.googleProfile) },
  { label: 'Map ranking', to: featureLink(locationRoutes.googleProfileMap) },
  { label: 'AI visibility', to: featureLink((b, l) => locationRoutes.aiVisibility(b, l)) },
  { label: 'Get-found tasks', to: featureLink(locationRoutes.aeo) },
  { label: 'Analytics', to: featureLink(locationRoutes.analytics) },
];

// Keyed on the full PlanKey union (matching how PLANS itself keeps the
// retired `starter` key, per CLAUDE.md's decoupling note) even though
// PLAN_ORDER below never renders it — an empty array there is inert, not a
// gap that needs an `as` cast at the lookup site.
const PLAN_FEATURE_LINKS: Record<PlanKey, FeatureLink[]> = {
  starter: [],
  pro: BASE_FEATURE_LINKS,
  business: [...BASE_FEATURE_LINKS, { label: 'Review replies', to: featureLink(locationRoutes.reviews) }],
  growth: [
    ...BASE_FEATURE_LINKS,
    { label: 'Review replies', to: featureLink(locationRoutes.reviews) },
    { label: 'Locations', to: (b) => businessRoutes.locations(b) },
  ],
};

type Interval = 'monthly' | 'annual';

const INTERVAL_OPTIONS: ReadonlyArray<{ value: Interval; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual · save 2mo' },
];

interface BillingData {
  configured: boolean;
  tier: string;
  pageLimit: number;
  sitesUsed: number;
  aiUsed: number;
  aiLimit: number;
  // Search-term capacity add-on (plain-units Task 6/7): packs held, effective
  // capacity (plan + packs x termsPerPack), and the pack's shape for copy.
  searchTermPacks: number;
  searchTermCapacity: number;
  searchTermAddon: { termsPerPack: number; priceCents: number; maxPacks: number };
  // Extra-location add-on (multi-location phase, Task 6): count held + the
  // add-on's shape for copy. Growth-only — see the Locations section below.
  extraLocations: number;
  locationAddon: { priceCents: number; maxExtra: number; includedForGrowth: number };
  // Survey response meter (escrow design, 2026-08-10). Plain units: how many
  // responses this month are visible, the capacity they count against
  // (plan + purchased packs), and how many are HELD waiting for capacity.
  surveyResponsesUsed: number;
  surveyResponsesLimit: number;
  surveyResponsesHeld: number;
  surveyResponsePack: { priceCents: number; packSize: number };
  subscription: {
    plan: PlanKey;
    interval: Interval;
    status: string;
    currentPeriodEnd: string | null;
    cancelAt: string | null;
    hasCustomer: boolean;
    // The REAL charged amount (deferred close-out Task 3, 2026-08-11). Null
    // for a comped org, a row the webhook hasn't touched yet, or a row that
    // predates this column — the CURRENT-plan card shows no figure then,
    // never PLANS[plan]'s list price (see chargedPriceDisplay).
    chargedAmountCents: number | null;
    chargedInterval: 'month' | 'year' | null;
  } | null;
}

// Repriced 2026-08-08 (operator-approved): the legacy `starter` key ($19/mo)
// is RETIRED — no new signups, no Stripe prices — and must never appear as a
// selectable plan card here. `pro` is the internal key kept unchanged; it now
// DISPLAYS as "Starter" ($99/mo) via PLANS.pro.label. See packages/shared/src/
// constants.ts PLANS for the single source of truth.
const PLAN_ORDER: PlanKey[] = ['pro', 'business', 'growth'];

// The same list, exported for billing.test.tsx's planCtaVariant cases so the
// rule is exercised against the lineup the grid actually renders rather than
// a copy that can drift.
export const PLAN_ORDER_FOR_TEST: readonly PlanKey[] = PLAN_ORDER;

function price(plan: PlanKey, interval: Interval): string {
  const cents = interval === 'monthly' ? PLANS[plan].monthlyPriceCents : PLANS[plan].annualPriceCents;
  return `$${(cents / 100).toFixed(0)}`;
}

/**
 * Design audit T8 (A11, one primary per screen). The plan grid handed every
 * non-current card the same filled "Switch" — so a downgrade shouted exactly
 * as loudly as an upgrade, and a customer on the middle plan saw two primaries
 * pulling in opposite directions. A card priced BELOW the customer's current
 * plan is a downgrade and renders outline; at or above it stays filled.
 *
 * Compares LIST prices (`PLANS`), not what this customer is actually charged:
 * Stripe prices are immutable, so a grandfathered subscriber can sit on an
 * amount that no longer matches any card, and "is this card up or down from my
 * tier?" is a question about tier order, not about their invoice. Monthly cents
 * are the yardstick because annual is a fixed 10x of monthly (locked #20) —
 * the toggle can never flip the direction.
 *
 * `current` null (no subscription) → nothing to go down from, so every card is
 * an upgrade.
 */
export function isPlanDowngrade(current: PlanKey | null, plan: PlanKey): boolean {
  if (current === null) return false;
  return PLANS[plan].monthlyPriceCents < PLANS[current].monthlyPriceCents;
}

/**
 * The tier we recommend to someone who has no plan to compare against. Not a
 * pricing decision — the middle card is simply the one a screen full of
 * equal-weight offers has to pick, and Business is the tier the pricing page
 * has always led with.
 */
export const RECOMMENDED_PLAN: PlanKey = 'business';

/**
 * Design audit T14 (T8's parked finding): with NO subscription every card
 * rendered a filled "Subscribe", so a screen whose rule is one filled button
 * shouted three times and recommended nothing. `isPlanDowngrade` could not
 * see this — with nothing to go down from, every card is an "upgrade".
 *
 * The same hole opens for a customer whose current plan is the RETIRED
 * `starter` key (locked decision #20): it is priced below every card on the
 * grid, so every card again reads as an upgrade. Both cases collapse to "we
 * have no plan of theirs to position against", and both get the same answer:
 * the recommended card is filled and the rest are outline.
 *
 * Adversarial round B4: "not a downgrade" was still too wide a net — a
 * customer on the lowest plan saw BOTH remaining cards filled, which is two
 * primaries again. Exactly ONE card is filled: the next tier up, the cheapest
 * step they can actually take. Every other card — the further upgrades and
 * every downgrade — is outline.
 *
 * `anchor` is the customer's current plan ONLY when that plan is one of the
 * three still on sale — the grid's own PLAN_ORDER is the authority, so this
 * never has to be re-derived if the lineup changes again.
 */
export function planCtaVariant(
  current: PlanKey | null,
  plan: PlanKey,
  onSale: readonly PlanKey[],
): 'default' | 'outline' {
  const anchor = current !== null && onSale.includes(current) ? current : null;
  if (anchor === null) return plan === RECOMMENDED_PLAN ? 'default' : 'outline';
  // Already on the top plan → there is no next step, so nothing is filled.
  const next = onSale[onSale.indexOf(anchor) + 1];
  return plan === next ? 'default' : 'outline';
}

export function Billing() {
  // Navigation preference only — see the FeatureLink note above.
  const [rememberedLocationId] = useState<string | null>(() => readActiveTenant()?.locationId ?? null);
  const meState = useMe();
  const [data, setData] = useState<BillingData | null>(null);
  const [interval, setInterval] = useState<Interval>('monthly');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Success feedback for the response-pack purchase: the number of held
  // responses ACTUALLY released, which a pack cannot promise up front.
  const [notice, setNotice] = useState<string | null>(null);
  // Invoice history (WP1 Task 13) — a separate fetch so a Stripe hiccup on
  // this read-only add-on never blocks the rest of the billing page.
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [searchParams] = useSearchParams();
  // T1.6B1: billing is a BUSINESS screen — its tenant is the one in the URL,
  // which the server proved on the request that rendered this page. It is NOT
  // the remembered "active business": a stored preference must never decide
  // which business gets charged.
  const businessId = useBusinessId() ?? '';

  // T12's UpgradeGate links here with businessRoutes.billing(businessId, { plan, from }) — read
  // both via URLSearchParams (what useSearchParams hands back).
  const highlightPlan = searchParams.get('plan');
  const backTo = safeInternalPath(searchParams.get('from'));

  useEffect(() => {
    api
      .get<BillingData>(businessApi(businessId, 'billing'))
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load billing.'));
  }, [businessId]);

  useEffect(() => {
    api
      .get<{ invoices: InvoiceRow[] }>(businessApi(businessId, 'billing/invoices'))
      .then((res) => setInvoices(res.invoices))
      .catch(() => setInvoices([]));
  }, [businessId]);

  async function subscribe(plan: PlanKey) {
    setBusy(plan);
    setError(null);
    try {
      const res = await api.post<{ url?: string; disabled?: boolean; error?: string }>(
        businessApi(businessId, 'billing/checkout'),
        { plan, interval },
      );
      if (res.url) window.location.href = res.url;
      else if (res.disabled)
        setError(
          "Checkout isn’t running on your account yet — that's on our side, not your card. Nothing has been charged; email support@pageboss.com and we'll get your plan started.",
        );
      else setError(res.error === 'plan_not_configured' ? 'That plan isn’t available yet.' : 'Could not start checkout.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
    } finally {
      setBusy(null);
    }
  }

  async function buyOverage() {
    setBusy('overage');
    setError(null);
    try {
      const res = await api.post<{ ok?: boolean; added?: number; disabled?: boolean; error?: string }>(
        businessApi(businessId, 'billing/overage'),
        {},
      );
      if (res.ok) {
        const fresh = await api.get<BillingData>(businessApi(businessId, 'billing'));
        setData(fresh);
      } else if (res.disabled) {
        setError('Billing isn’t enabled yet.');
      } else if (res.error === 'no_customer') {
        setError('Subscribe to a plan first to buy extra AI generations.');
      } else {
        setError('Could not complete the purchase.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not complete the purchase.');
    } finally {
      setBusy(null);
    }
  }

  // Add or remove ONE search-term pack. Takes a DELTA, not an absolute
  // target: the server wants an absolute count, so we re-read billing first
  // and compute from the fresh number — a page that sat open while another
  // tab changed packs must not turn "Remove" into a silent ADD (its stale 3
  // minus 1 posted against a fresh 0 would set 2). The confirm also shows the
  // fresh numbers. The server still holds every rule — max packs, paid tier,
  // terms-in-use on a decrease, its own CAS against concurrent writes.
  async function changeSearchTermPacks(delta: 1 | -1) {
    if (!data) return;
    setBusy('packs');
    setError(null);
    try {
      const fresh = await api.get<BillingData>(businessApi(businessId, 'billing'));
      setData(fresh);
      const next = fresh.searchTermPacks + delta;
      if (next < 0 || next > fresh.searchTermAddon.maxPacks) return; // stale button — state now refreshed
      const per = fresh.searchTermAddon.termsPerPack;
      const dollars = (fresh.searchTermAddon.priceCents / 100).toFixed(0);
      // Capacity language, not "tracked terms": a pack changes how much ROOM
      // the plan has; which terms are tracked is set on the Map ranking page
      // and a removal never untracks anything (the server refuses instead).
      const ok = window.confirm(
        delta > 0
          ? `Add room for ${per} more search terms — $${dollars}/month?`
          : `Remove the extra pack of ${per}? Your tracked search terms stay as they are, and the $${dollars}/month charge stops.`,
      );
      if (!ok) return;
      await api.post(businessApi(businessId, 'billing/search-term-packs'), { packs: next });
      setData(await api.get<BillingData>(businessApi(businessId, 'billing')));
    } catch (e) {
      const body = (e as { body?: unknown }).body;
      const code =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : null;
      setError(
        code === 'capacity_in_use'
          ? "You're tracking more search terms than you'd have room for without this pack. Remove some search terms on the Map ranking page first, then try again."
          : code === 'conflict'
            ? 'Your packs changed somewhere else while this was saving — nothing was applied. The numbers here are refreshed; try again.'
            : code === 'pack_not_configured' || code === 'billing_failed'
              ? "We couldn't change this just now — nothing changed on your plan and nothing new is being charged. Please try again in a bit."
              : 'Could not change your search-term packs — nothing changed on your plan.',
      );
    } finally {
      setBusy(null);
    }
  }

  // Add or remove ONE extra location. Same fresh-read-then-delta shape as
  // changeSearchTermPacks above, for the same reason: the server wants an
  // absolute count, so we re-read billing first and compute the next value
  // from the fresh number — a page that sat open while another tab changed
  // the count must not turn "Remove" into a silent ADD. The server still
  // holds every rule — Growth-only, max extra, group-in-use on a decrease,
  // its own CAS against concurrent writes.
  async function changeLocations(delta: 1 | -1) {
    if (!data) return;
    setBusy('locations');
    setError(null);
    try {
      const fresh = await api.get<BillingData>(businessApi(businessId, 'billing'));
      setData(fresh);
      const next = fresh.extraLocations + delta;
      if (next < 0 || next > fresh.locationAddon.maxExtra) return; // stale button — state now refreshed
      const dollars = (fresh.locationAddon.priceCents / 100).toFixed(0);
      const ok = window.confirm(
        delta > 0
          ? `Add room for one more business location — $${dollars}/month?`
          : `Remove one extra location slot and stop its $${dollars}/month charge? Your sites and their data stay — if a group is still using every slot we'll say so instead of removing anything.`,
      );
      if (!ok) return;
      await api.post(businessApi(businessId, 'billing/locations'), { extraLocations: next });
      setData(await api.get<BillingData>(businessApi(businessId, 'billing')));
    } catch (e) {
      const body = (e as { body?: unknown }).body;
      const code =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : null;
      setError(
        code === 'locations_in_use'
          ? 'A location group is still using every slot. Detach a site from the group first, then try again.'
          : code === 'conflict'
            ? 'Your extra locations changed somewhere else while this was saving — nothing was applied. The numbers here are refreshed; try again.'
            : code === 'addon_not_configured' || code === 'billing_failed'
              ? "We couldn't change this just now — nothing changed on your plan and nothing new is being charged. Please try again in a bit."
              : code === 'locations_not_in_plan' || code === 'no_subscription'
                ? 'Extra locations come with the Growth plan.'
                : 'Could not change your extra locations — nothing changed on your plan.',
      );
    } finally {
      setBusy(null);
    }
  }

  // Toggle the +$10/mo survey add-on (absolute desired state — idempotent).
  // The entitlement lives on /api/me (usage.survey), so success invalidates it;
  // the CSV/publish/invite gates across the app pick the change up on their
  // next read.
  async function setSurveyAddon(active: boolean) {
    const dollars = (SURVEY_ADDON.priceCents / 100).toFixed(0);
    const ok = window.confirm(
      active
        ? `Add surveys to your plan for $${dollars}/month? Full limits for your plan unlock right away.`
        : `Remove the survey add-on and stop the $${dollars}/month charge? Published surveys stay up; new publishes, CSV export and email invites drop back to the free taste.`,
    );
    if (!ok) return;
    setBusy('survey');
    setError(null);
    try {
      await api.post(businessApi(businessId, 'billing/survey-addon'), { active });
      invalidateMe();
    } catch (e) {
      const body = (e as { body?: unknown }).body;
      const code =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : null;
      setError(
        code === 'conflict'
          ? 'This changed somewhere else while saving — nothing was applied. Try again.'
          : code === 'addon_not_configured' || code === 'billing_failed'
            ? "We couldn't change this just now — nothing changed on your plan and nothing new is being charged. Please try again in a bit."
            : code === 'survey_addon_needs_paid_plan' || code === 'no_subscription'
              ? 'The survey add-on needs an active paid plan.'
              : 'Could not change the survey add-on — nothing changed on your plan.',
      );
    } finally {
      setBusy(null);
    }
  }

  // One-time $99 white-label unlock. The server is idempotent by state (a
  // second call is a free no-op), and grant_failed carries charged:true — the
  // one case where "nothing changed" would be a lie, so the copy says so.
  async function buySurveyWhiteLabel() {
    const dollars = (SURVEY_WHITE_LABEL.priceCents / 100).toFixed(0);
    const ok = window.confirm(
      `Remove "Powered by pageboss" from all your surveys for a one-time $${dollars}? This stays yours even if you change plans.`,
    );
    if (!ok) return;
    setBusy('survey-wl');
    setError(null);
    try {
      const res = await api.post<{ ok?: boolean; disabled?: boolean }>(
        businessApi(businessId, 'billing/survey-white-label'),
        { idempotencyKey: crypto.randomUUID().replace(/-/g, '') },
      );
      if (res.disabled) {
        setError("Billing isn't enabled yet.");
        return;
      }
      invalidateMe();
    } catch (e) {
      const body = (e as { body?: unknown }).body;
      const code =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : null;
      setError(
        code === 'grant_failed'
          ? 'Your card was charged but the unlock did not apply — contact support@pageboss.com and we will fix it right away.'
          : code === 'survey_addon_required'
            ? 'White-label needs the survey add-on active first.'
            : code === 'no_customer'
              ? 'Subscribe to a plan first.'
              : 'Could not complete the purchase — nothing changed.',
      );
    } finally {
      setBusy(null);
    }
  }

  // Buy one $9 / 1,000-response pack for THIS month. The server charges (or
  // skips billing for a comped org), grants, and releases held responses in
  // one call, then hands back the fresh meter — so we just re-read billing.
  async function buySurveyResponsePack() {
    if (!data) return;
    const dollars = (data.surveyResponsePack.priceCents / 100).toFixed(0);
    const size = data.surveyResponsePack.packSize.toLocaleString();
    const held = data.surveyResponsesHeld;
    // A pack adds exactly `packSize` of capacity, and the held backlog is
    // unbounded — so the promise is "up to N of your M", never "all of them".
    const ok = window.confirm(
      held > 0
        ? `Add ${size} responses to this month for $${dollars}? Up to ${size} of the ${held.toLocaleString()} responses waiting on capacity — the ones waiting longest — show up right away.`
        : `Add ${size} responses to this month for $${dollars}? They're for this month only — next month starts fresh on your plan's allowance.`,
    );
    if (!ok) return;
    setBusy('survey-pack');
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ ok?: boolean; released?: number }>(
        businessApi(businessId, 'billing/survey-response-pack'),
        { idempotencyKey: crypto.randomUUID().replace(/-/g, '') },
      );
      if (res.ok) {
        const fresh = await api.get<BillingData>(businessApi(businessId, 'billing'));
        setData(fresh);
        // Report what ACTUALLY happened, not what was hoped for: the server
        // returns the real released count, and any remainder is drained
        // automatically as room appears.
        const n = res.released ?? 0;
        setNotice(
          n > 0
            ? `${n.toLocaleString()} held response${n === 1 ? '' : 's'} released.${
                fresh.surveyResponsesHeld > 0
                  ? ` ${fresh.surveyResponsesHeld.toLocaleString()} still waiting on capacity.`
                  : ''
              }`
            : `Added ${size} responses to this month.`,
        );
      }
    } catch (e) {
      const body = (e as { body?: unknown }).body;
      const code =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : null;
      // The server tells us whether a card was actually charged — a comped org
      // (no Stripe customer) is granted unbilled, and must never be told about
      // a charge that does not exist.
      const charged =
        body && typeof body === 'object' && 'charged' in body
          ? Boolean((body as { charged: unknown }).charged)
          : false;
      setError(
        code === 'grant_failed'
          ? charged
            ? 'Your card was charged but the responses were not added — contact support@pageboss.com and we will fix it right away.'
            : 'The responses were not added — nothing was charged. Contact support@pageboss.com and we will sort it out.'
          : code === 'survey_addon_required'
            ? 'Response packs need the survey add-on active first.'
            : code === 'survey_pack_needs_paid_plan'
              ? 'Response packs need an active paid plan.'
              : code === 'pack_not_configured'
                ? "Billing isn't enabled yet — nothing was charged."
                : 'Could not complete the purchase — nothing changed.',
      );
    } finally {
      setBusy(null);
    }
  }

  async function manage() {
    setBusy('portal');
    setError(null);
    try {
      const res = await api.post<{ url?: string; disabled?: boolean }>(businessApi(businessId, 'billing/portal'), {});
      if (res.url) window.location.href = res.url;
      else setError('Billing portal isn’t available.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the billing portal.');
    } finally {
      setBusy(null);
    }
  }

  if (meState.kind === 'error') {
    // Billing had NO error branch at all — a failed /api/me rendered a blank
    // white screen on the one page a customer opens when something is wrong
    // with their account. Same eyebrow and <h1> as the ready state below.
    return (
      <div className="px-6 py-8">
        {/* NOT wrapped in <PanelShell>: the shell reads the sidebar out of the
            very /api/me payload that just failed, and its own error branch
            returns BEFORE its children — mounting it here would replace this
            header with the shell's bare paragraph and lose exactly the page
            identity this exists to keep. The wrapper restores the gutter
            PanelShell's <main> would have supplied (PageContainer sets none). */}
        <PageError
          eyebrow="Account"
          title="Billing"
          message={meState.message}
          retry={invalidateMe}
        />
      </div>
    );
  }
  if (meState.kind !== 'ready') return null;
  const me = meState.me;
  const current = data?.subscription;

  return (
    <PanelShell>
      {/* The bespoke `px-6 py-8` wrapper is gone — PanelShell's <main>
          already supplies the gutter (design audit P1-9). */}
      <PageContainer>
        {backTo ? (
          <Link
            to={backTo}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        ) : null}
        <PageHeader
          eyebrow="Account"
          title="Billing"
          description={`Manage your plan and payment. ${data && !data.configured ? 'Online billing is being set up — plans below are preview-only for now.' : 'Upgrade any time.'}`}
        />

        {error ? (
          <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{error}</div>
        ) : null}

        {notice ? (
          <div className="rounded-md bg-success-bg px-3 py-2 text-sm text-success-fg" role="status">
            {notice}
          </div>
        ) : null}

        {current?.status === 'past_due' ? (
          <div className="rounded-md bg-warning-bg px-4 py-3 text-sm text-warning-fg">
            <strong>Payment failed.</strong> Update your card to keep your site online — after a
            grace period it's temporarily held, and deactivated at 30 days.{' '}
            {current.hasCustomer ? (
              <Button
                type="button"
                variant="link"
                onClick={() => void manage()}
                className="h-auto min-h-6 p-0 align-baseline text-warning-fg underline"
              >
                Update payment
              </Button>
            ) : null}
          </div>
        ) : null}

        {!data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between rounded-lg border bg-card px-4 py-3">
              <div>
                <div className="text-sm text-muted-foreground">Current plan</div>
                <div className="flex items-center gap-2 text-lg font-semibold">
                  {current ? PLANS[current.plan].label : 'Free preview'}
                  {current ? (
                    <Badge variant={current.status === 'active' ? 'success' : 'muted'}>
                      {current.status}
                    </Badge>
                  ) : null}
                </div>
                {current?.cancelAt ? (
                  <div className="text-xs text-warning-fg">
                    Cancels {new Date(current.cancelAt).toLocaleDateString()}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right text-xs text-muted-foreground">
                  <div>
                    AI this month:{' '}
                    <span className="font-medium tabular-nums text-foreground">
                      {data.aiUsed}
                      {data.aiLimit > 0 ? ` / ${data.aiLimit}` : ''}
                    </span>
                  </div>
                  {data.aiLimit === 0 ? <div>AI generation is included on every paid plan</div> : null}
                </div>
                {data.aiLimit > 0 && data.configured && current?.hasCustomer ? (
                  <Button variant="outline" disabled={busy === 'overage'} onClick={() => void buyOverage()}>
                    {busy === 'overage' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Buy 100 more · $9
                  </Button>
                ) : null}
                {current?.hasCustomer ? (
                  <Button variant="outline" disabled={busy === 'portal'} onClick={() => void manage()}>
                    {busy === 'portal' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    Manage billing
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Search-term tracking capacity (plain-units Task 7). Rendered
                only for tiers whose plan includes search terms; plain units
                and dollar prices only (locked #11). Comped subscriptions (no
                Stripe customer) still get the controls — the server applies
                the change without billing for them. */}
            {data.searchTermCapacity > 0 && current ? (
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
                <div>
                  <div className="text-sm text-muted-foreground">Map ranking</div>
                  <div className="text-sm font-medium">
                    Tracking up to{' '}
                    <span className="tabular-nums">{data.searchTermCapacity}</span> search terms
                    {data.searchTermPacks > 0
                      ? ` — includes ${data.searchTermPacks} extra ${
                          data.searchTermPacks === 1 ? 'pack' : 'packs'
                        } of ${data.searchTermAddon.termsPerPack} at $${(
                          data.searchTermAddon.priceCents / 100
                        ).toFixed(0)}/mo each`
                      : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {data.searchTermPacks > 0 ? (
                    <Button
                      variant="outline"
                      disabled={busy === 'packs'}
                      onClick={() => void changeSearchTermPacks(-1)}
                    >
                      {busy === 'packs' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Remove a pack
                    </Button>
                  ) : null}
                  {data.searchTermPacks < data.searchTermAddon.maxPacks ? (
                    <Button
                      variant="outline"
                      disabled={busy === 'packs'}
                      onClick={() => void changeSearchTermPacks(1)}
                    >
                      {busy === 'packs' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Add {data.searchTermAddon.termsPerPack} more · $
                      {(data.searchTermAddon.priceCents / 100).toFixed(0)}/mo
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Maximum extra search terms reached
                    </span>
                  )}
                </div>
              </div>
            ) : null}

            {/* Extra-location add-on (multi-location phase, Task 6). Growth-only
                — the plan's included 3 locations are the only base a group can
                grow from, so the section renders only when the effective tier
                is growth. Comped subscriptions (no Stripe customer) still get
                the controls — the server applies the change without billing
                for them, same as the search-term pack section above. */}
            {data.tier === 'growth' && current ? (
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
                <div>
                  <div className="text-sm text-muted-foreground">Locations</div>
                  <div className="text-sm font-medium">
                    Up to{' '}
                    <span className="tabular-nums">
                      {data.locationAddon.includedForGrowth + data.extraLocations}
                    </span>{' '}
                    business locations
                    {data.extraLocations > 0
                      ? ` — includes ${data.extraLocations} extra at $${(
                          data.locationAddon.priceCents / 100
                        ).toFixed(0)}/mo each`
                      : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {data.extraLocations > 0 ? (
                    <Button
                      variant="outline"
                      disabled={busy === 'locations'}
                      onClick={() => void changeLocations(-1)}
                    >
                      {busy === 'locations' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Remove a location
                    </Button>
                  ) : null}
                  {data.extraLocations < data.locationAddon.maxExtra ? (
                    <Button
                      variant="outline"
                      disabled={busy === 'locations'}
                      onClick={() => void changeLocations(1)}
                    >
                      {busy === 'locations' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Add a location · $
                      {(data.locationAddon.priceCents / 100).toFixed(0)}/mo
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Maximum extra locations reached
                    </span>
                  )}
                </div>
              </div>
            ) : null}

            {/* Survey add-on (+$10/mo boolean item) + one-time $99 white-label.
                Entitlement comes from /api/me (usage.survey) — the same source
                every survey gate reads, so this section can't drift from what
                the product enforces. `data.tier` (the EFFECTIVE tier — canceled
                and held subs resolve to 'free') decides whether the buy buttons
                render: a dead-end CTA that can only 400 helps nobody. The
                free-tier text keeps the trail from the locked CSV button
                honest — it names the real prerequisite instead of hiding. */}
            {me.usage.survey ? (
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
                <div>
                  <div className="text-sm text-muted-foreground">Surveys</div>
                  <div className="text-sm font-medium">
                    {me.usage.survey.addonActive
                      ? me.usage.survey.whiteLabel
                        ? 'Survey add-on active — white-label included'
                        : 'Survey add-on active — full limits for your plan'
                      : 'Free taste: 1 published survey, 25 responses'}
                  </div>
                  {/* Response meter — always visible (the design's warning #1),
                      teaser included: a teaser org meters against 25 exactly the
                      same way. Plain units only; the held count says what escrow
                      is actually doing instead of quietly losing responses. */}
                  <div className="mt-2 max-w-xs">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="tabular-nums">
                        {/* "used", not "collected": a response released from
                            hold counts against the month it was released in,
                            which may not be the month it was answered in. */}
                        {data.surveyResponsesUsed.toLocaleString()} of{' '}
                        {data.surveyResponsesLimit.toLocaleString()} responses used this month
                      </span>
                      {data.surveyResponsesHeld > 0 ? (
                        <Badge variant="muted">
                          +{data.surveyResponsesHeld.toLocaleString()} held
                        </Badge>
                      ) : null}
                    </div>
                    <div
                      className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={data.surveyResponsesLimit}
                      aria-valuenow={Math.min(data.surveyResponsesUsed, data.surveyResponsesLimit)}
                      aria-label="Survey responses used this month"
                    >
                      {/* Width is a runtime percentage — no Tailwind class can
                          express it; same inline-width idiom as get-found-score. */}
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{
                          width: `${
                            data.surveyResponsesLimit > 0
                              ? Math.min(
                                  100,
                                  Math.round(
                                    (data.surveyResponsesUsed / data.surveyResponsesLimit) * 100,
                                  ),
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    {data.surveyResponsesHeld > 0 ? (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {/* The pack CTA is only honest for an org that can
                            actually buy one — the purchase needs a paid plan
                            AND the survey add-on. Everyone else gets the real
                            prerequisite instead of a button they won't find. */}
                        Those responses are safely recorded.{' '}
                        {me.usage.survey.addonActive && data.tier !== 'free' && current
                          ? `Add ${data.surveyResponsePack.packSize.toLocaleString()} responses to see the oldest of them now, or they start showing up as next month's allowance frees room.`
                          : "They start showing up as next month's allowance frees room — or add surveys to your plan for a bigger monthly allowance."}
                      </p>
                    ) : null}
                  </div>
                </div>
                {data.tier === 'free' || !current ? (
                  <span className="text-xs text-muted-foreground">
                    The survey add-on needs an active paid plan — pick one below.
                  </span>
                ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {me.usage.survey.addonActive ? (
                    <>
                      {/* Response pack. Shown only where the purchase can
                          actually succeed: a Stripe-billed org needs billing
                          configured, a comped org (no Stripe customer) is
                          granted unbilled by the server — same comp handling as
                          the search-term and location sections. */}
                      {data.configured || !current.hasCustomer ? (
                        <Button
                          variant="outline"
                          disabled={busy === 'survey-pack'}
                          onClick={() => void buySurveyResponsePack()}
                        >
                          {busy === 'survey-pack' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : null}
                          Add {data.surveyResponsePack.packSize.toLocaleString()} responses · $
                          {(data.surveyResponsePack.priceCents / 100).toFixed(0)}
                        </Button>
                      ) : null}
                      {!me.usage.survey.whiteLabel ? (
                        <Button
                          variant="outline"
                          disabled={busy === 'survey-wl'}
                          onClick={() => void buySurveyWhiteLabel()}
                        >
                          {busy === 'survey-wl' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Remove our badge · ${(SURVEY_WHITE_LABEL.priceCents / 100).toFixed(0)} once
                        </Button>
                      ) : null}
                      <Button
                        variant="outline"
                        disabled={busy === 'survey'}
                        onClick={() => void setSurveyAddon(false)}
                      >
                        {busy === 'survey' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Remove add-on
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={busy === 'survey'}
                      onClick={() => void setSurveyAddon(true)}
                    >
                      {busy === 'survey' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Add surveys · ${(SURVEY_ADDON.priceCents / 100).toFixed(0)}/mo
                    </Button>
                  )}
                </div>
                )}
              </div>
            ) : null}

            {/* Design audit T7: hand-rolled pair with no radiogroup roles and
                an `bg-accent` selected state → the shared SegmentedControl. */}
            <SegmentedControl<Interval>
              ariaLabel="Billing cycle"
              value={interval}
              options={INTERVAL_OPTIONS}
              onChange={setInterval}
              className="mb-4"
            />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PLAN_ORDER.map((plan) => {
                // isCurrent (badge/button — "Current" disabled button) stays
                // gated on status === 'active', unchanged. isCurrentPlan
                // (what governs the PRICE shown) is gated on the plan match
                // ALONE: a grandfathered subscriber in dunning (e.g.
                // past_due) is still on this plan and must never see the
                // list price on the card the header two rows up already
                // labels as theirs — that reads as a claim, not an offer
                // (fix round 1, 2026-08-11).
                const isCurrent = current?.plan === plan && current.status === 'active';
                const isCurrentPlan = current?.plan === plan;
                // The CURRENT-plan card shows what this customer actually
                // pays, never the list price: Stripe prices are immutable, so
                // a repricing can leave an existing subscriber on a price
                // that no longer matches PLANS[plan] at all. Unknown → no
                // figure, not a guess (operator decision, 2026-08-10). This
                // is deliberately independent of the monthly/annual toggle
                // above — that toggle previews what SWITCHING would cost,
                // not what the current plan bills.
                const charged =
                  current && current.plan === plan
                    ? chargedPriceDisplay(current.chargedAmountCents, current.chargedInterval)
                    : null;
                // ?plan= highlight (T12's UpgradeGate deep-links here with
                // the plan the owner needs) — a visual ring, not a behavior
                // change; the card is otherwise identical.
                const isHighlighted = highlightPlan === plan;
                return (
                  <div
                    key={plan}
                    // Test hook, same convention as overview.tsx's [data-layer]:
                    // the card's own label is a DISPLAY name ("Starter" is the
                    // `pro` key — CLAUDE.md #20's trap), so a test must never
                    // find a card by matching label text to a key.
                    data-plan={plan}
                    className={
                      'flex flex-col rounded-lg border bg-card p-4' +
                      (isHighlighted ? ' ring-2 ring-brand-500 ring-offset-2 ring-offset-background' : '')
                    }
                  >
                    <div className="font-semibold">{PLANS[plan].label}</div>
                    <div className="mt-1 text-2xl font-bold">
                      {isCurrentPlan ? (
                        charged ? (
                          <>
                            {/* formatChargedPrice never rounds — this is an
                                arbitrary historical Stripe amount, not a
                                curated PLANS constant (fix round 1). */}
                            {formatChargedPrice(charged.amountCents)}
                            <span className="text-sm font-normal text-muted-foreground">
                              /{charged.interval === 'month' ? 'mo' : 'yr'}
                            </span>
                          </>
                        ) : (
                          // Comped org, a row the webhook hasn't touched yet,
                          // or a row that predates this column — say so
                          // instead of rendering nothing, which collapsed
                          // this div's height and read as a bug (fix round 1).
                          <span className="text-sm font-normal text-muted-foreground">Your plan</span>
                        )
                      ) : (
                        <>
                          {price(plan, interval)}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{interval === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {`${PLANS[plan].pageLimit} pages`}
                      {PLANS[plan].aiGenerationsMonthly > 0 ? ` · ${PLANS[plan].aiGenerationsMonthly} AI/mo` : ''}
                      {PLANS[plan].customDomain ? ' · custom domain' : ''}
                    </div>
                    {/* Outbound feature links (killing the sink, WP1 Task 13)
                        — real navigation into what this plan includes, not
                        more prose. Falls back to /app when there's no active
                        business yet (a brand-new signup). */}
                    {/* Design audit T8: eighteen rust link labels across three
                        cards competed with the cards' own CTAs for attention,
                        and an arrow GLYPH ("→") in the label is emoji-as-icon
                        by another name. Neutral label + a real lucide chevron
                        carrying the brand colour reads as navigation without
                        shouting. */}
                    <ul className="mt-3 space-y-1 border-t pt-3 text-xs">
                      {PLAN_FEATURE_LINKS[plan].map((feature) => (
                        <li key={feature.label}>
                          {/* T11/A7: 12 px labels gave these 18 links a
                              16 px-tall hit area. min-h only — the label
                              text size is unchanged. */}
                          <Link
                            to={businessId ? feature.to(businessId, rememberedLocationId) : '/app'}
                            className="inline-flex min-h-6 items-center gap-1 text-text-secondary transition-colors hover:text-foreground hover:underline pointer-coarse:min-h-11"
                          >
                            {feature.label}
                            <ChevronRight
                              className="h-3 w-3 shrink-0 text-brand-700"
                              aria-hidden="true"
                            />
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-4">
                      {isCurrent ? (
                        <Button variant="outline" disabled className="w-full">
                          <Check className="h-4 w-4" /> Current
                        </Button>
                      ) : (
                        <Button
                          className="w-full"
                          variant={planCtaVariant(current?.plan ?? null, plan, PLAN_ORDER)}
                          disabled={busy === plan}
                          onClick={() => void subscribe(plan)}
                        >
                          {busy === plan ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {current ? 'Switch' : 'Subscribe'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              30-day money-back guarantee · no free trial · cancel any time.{' '}
              <a href="https://pageboss.com/terms" target="_blank" rel="noreferrer" className="inline-flex min-h-6 items-center gap-0.5 underline pointer-coarse:min-h-11">
                Terms <ExternalLink className="h-3 w-3" />
              </a>
            </p>

            {/* Invoice history (WP1 Task 13) — read-only, GET /api/billing/
                invoices. Only shown once there's a Stripe customer to have
                invoices at all (same gate as "Manage billing" above); an
                empty list (no invoices yet, or billing not configured) says
                so plainly rather than rendering a misleading blank card. */}
            {current?.hasCustomer ? (
              <div className="mt-8 rounded-lg border bg-card">
                <div className="border-b px-4 py-3">
                  <h2 className="text-sm font-semibold">Invoice history</h2>
                </div>
                {invoices === null ? (
                  <div className="p-4">
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : invoices.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-muted-foreground">No invoices yet.</p>
                ) : (
                  <ul className="divide-y">
                    {invoices.map((inv) => (
                      <li
                        key={inv.id}
                        className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                      >
                        <div className="flex items-center gap-3">
                          <span className="tabular-nums text-muted-foreground">
                            {new Date(inv.created * 1000).toLocaleDateString()}
                          </span>
                          <span className="font-medium">
                            {inv.number ?? inv.id}
                          </span>
                          <Badge variant={invoiceStatusVariant(inv.status)}>{inv.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="tabular-nums font-medium">
                            {formatInvoiceAmount(inv.total, inv.currency)}
                          </span>
                          {inv.hostedInvoiceUrl ? (
                            <a
                              href={inv.hostedInvoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-0.5 text-brand-700 hover:text-brand-800 hover:underline"
                            >
                              view <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </>
        )}
      </PageContainer>
    </PanelShell>
  );
}
